import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  ArrowLeft,
  Sprout,
  Truck,
  GraduationCap,
  CheckCircle2,
  Building2,
  Phone,
  Mail,
  Globe,
  Trees,
  Microscope,
  Database,
} from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";

const PDF_URL =
  "https://customer-assets.emergentagent.com/job_country-gardens/artifacts/xdnme7ri_Policy%20Briefing-%20growing%20country.pdf";

const strategic = [
  "Improved remote food security",
  "Reduced reliance on fragile supply chains",
  "Better nutrition and health outcomes",
  "Climate adaptation and resilience",
  "Indigenous economic participation",
  "Community-led development",
  "Local training and employment pathways",
  "Cultural knowledge transfer",
  "Biodiversity enhancement",
  "Soil and ecosystem restoration",
  "Increased community capability",
];

const policy = [
  "Closing the Gap priorities",
  "National food security discussions",
  "Climate adaptation and resilience strategies",
  "Regional development frameworks",
  "Indigenous economic participation priorities",
  "Community health and wellbeing initiatives",
  "Environmental restoration and biodiversity objectives",
  "Indigenous-led development approaches",
];

const pilotComponents = [
  "Regenerative orchards and food forests",
  "Community-scale food production systems",
  "Mobile Growing Country support units",
  "Shade houses and climate-adapted growing infrastructure",
  "Water harvesting and irrigation systems",
  "Community seed banks and nurseries",
  "Composting and soil improvement programs",
  "Training and employment pathways",
  "Ranger-supported food resilience projects",
  "Community food resilience planning",
];

const partners = [
  "Government agencies",
  "Indigenous organisations",
  "Philanthropic foundations",
  "Climate adaptation programs",
  "Research institutions",
  "Agricultural and horticultural partners",
  "Regional development organisations",
  "Health and wellbeing initiatives",
  "Environmental and restoration programs",
];

const model = [
  {
    icon: Trees,
    title: "Regenerative Orchard & Food Forest Systems",
    body: "Long-term perennial food production systems incorporating fruit trees, bush foods, medicinal plants, pollinator habitat and shade infrastructure — improving nutrition, biodiversity, microclimate regulation and community resilience while creating productive landscapes for future generations.",
  },
  {
    icon: Sprout,
    title: "Community Growing Networks",
    body: "Locally designed food production areas supporting access to fresh produce while creating opportunities for community participation, intergenerational learning and local leadership.",
  },
  {
    icon: Truck,
    title: "Mobile Growing Country Units",
    body: "Mobile horticultural support vehicles delivering technical assistance, training, infrastructure development and on-country mentoring across participating communities — practical support for establishing and maintaining growing systems in remote regions.",
  },
  {
    icon: Database,
    title: "Seed Systems & Propagation",
    body: "Community seed banks, nurseries and propagation systems supporting local food production, climate adaptation and long-term planting programs — helping communities maintain local planting stock while strengthening knowledge transfer.",
  },
  {
    icon: Microscope,
    title: "Soil Health & Biological Systems",
    body: "Composting, soil biology, beneficial insects, water harvesting and regenerative growing practices that reduce reliance on external inputs while improving productivity and resilience. Healthy soils, functioning ecosystems and biological diversity are recognised as critical foundations of long-term food security.",
  },
  {
    icon: GraduationCap,
    title: "Training, Employment & Enterprise Development",
    body: "Practical pathways supporting ranger programs, youth engagement, horticultural skills, community enterprises and Indigenous-led food production initiatives.",
  },
];

export default function PolicyBriefing() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <main data-testid="policy-briefing-page" className="bg-sand text-charcoal">
      <Navigation scrolled={scrolled} />

      {/* COVER */}
      <section
        data-testid="briefing-cover"
        className="relative min-h-[90vh] flex items-center bg-charcoal text-sand overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/32915492/pexels-photo-32915492.jpeg"
            alt="Australian outback aerial"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/85 via-charcoal/70 to-charcoal" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40 w-full">
          <Link
            to="/"
            data-testid="briefing-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-sand/60 hover:text-ochre transition-colors mb-12"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back to Growing Country
          </Link>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="text-xs md:text-sm uppercase tracking-[0.32em] text-ochre mb-6 flex items-center gap-4">
                <span className="inline-block w-10 h-px bg-ochre" />
                Policy Briefing
              </div>

              <h1
                data-testid="briefing-title"
                className="font-serif font-light text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight"
              >
                Building Australia's
                <span className="block italic text-ochre">
                  Indigenous food future.
                </span>
              </h1>

              <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed text-sand/80">
                A national Indigenous food resilience initiative designed to
                strengthen local food production, improve community resilience
                and support climate-adapted growing systems across remote
                Australia.
              </p>

              <div className="mt-12 flex flex-wrap items-center gap-4">
                <a
                  href={PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="briefing-download-pdf"
                  className="inline-flex items-center gap-3 bg-rust text-sand px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-rust-dark transition-colors"
                >
                  <Download className="w-4 h-4" strokeWidth={1.5} />
                  Download briefing (PDF)
                </a>
                <a
                  href="/#contact"
                  data-testid="briefing-cta-discuss"
                  className="inline-flex items-center gap-3 border border-sand/60 text-sand px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-sand hover:text-charcoal transition-colors"
                >
                  Discuss this with us
                </a>
              </div>
            </div>

            <div className="md:col-span-4 md:pl-8">
              <div className="border-l border-ochre/40 pl-6 space-y-6">
                <Meta k="Document" v="Policy Briefing" />
                <Meta k="Authored by" v="William Martin" />
                <Meta k="Scope" v="National pilot proposal" />
                <Meta k="Sectors" v="Food security · Climate · Indigenous economic participation" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <Section id="overview" eyebrow="Overview" data-testid="briefing-overview">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
              A national, Indigenous-led
              <span className="block italic text-rust">food future.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-6 text-base md:text-lg leading-relaxed text-ink">
            <p>
              Growing Country is a national Indigenous food resilience
              initiative designed to strengthen local food production, improve
              community resilience and support climate-adapted food systems
              across remote Australia.
            </p>
            <p>
              The initiative responds to increasing concern surrounding remote
              food insecurity, fragile supply chains, rising freight costs and
              climate-related disruption affecting Aboriginal and Torres Strait
              Islander communities.
            </p>
            <p>
              Growing Country proposes a practical and scalable national pilot
              focused on rebuilding local food capability through community-led
              ecological food systems, regenerative orchards, food forests,
              mobile horticultural support and long-term skills development.
            </p>
          </div>
        </div>
      </Section>

      {/* NATIONAL CHALLENGE */}
      <Section
        eyebrow="01 · The National Challenge"
        tone="alt"
        data-testid="briefing-challenge"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
              Sitting at the most exposed end of
              <span className="block italic text-rust">
                Australia's food system.
              </span>
            </h2>
            <div className="mt-10 space-y-6 text-base md:text-lg leading-relaxed text-ink max-w-2xl">
              <p>
                Australia is one of the world's largest food-producing nations,
                yet many remote Aboriginal and Torres Strait Islander
                communities remain highly vulnerable to food insecurity.
              </p>
              <p>
                Remote communities sit at the most exposed end of Australia's
                food system, where supply chain disruption, fuel volatility,
                freight costs and climate impacts are experienced first and
                hardest.
              </p>
              <p>
                Climate change, extreme weather events, rising transport costs
                and ongoing reliance on imported food continue to expose
                vulnerabilities across remote Australia.
              </p>
              <p>
                Growing Country has been developed to strengthen local
                resilience within this increasingly fragile environment by
                rebuilding the systems that support community-scale food
                production.
              </p>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="bg-charcoal text-sand p-10 md:p-12 relative overflow-hidden h-full">
              <div className="absolute inset-0 grain-overlay opacity-50" />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-[0.28em] text-ochre/85 mb-4">
                  Core Insight
                </div>
                <p className="font-serif text-3xl md:text-4xl italic font-light leading-tight">
                  Australia's food system has been optimised for efficiency
                  rather than resilience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* MODEL */}
      <Section
        eyebrow="02 · The Growing Country Model"
        data-testid="briefing-model"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-7">
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
              Six connected components,
              <span className="block italic text-sage">
                one ecological food system.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base md:text-lg leading-relaxed text-ink">
              Growing Country supports the development of community-scale
              ecological food systems designed specifically for remote
              Australian conditions. Rather than focusing solely on annual food
              production, the initiative combines regenerative orchards, food
              forests, community growing systems, seed infrastructure and
              climate-adapted land management to build long-term food
              resilience.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-line border border-stone-line">
          {model.map((m, i) => {
            const Icon = m.icon;
            return (
              <div
                key={m.title}
                data-testid={`briefing-model-${i}`}
                className="bg-sand p-10 md:p-12 group hover:bg-charcoal hover:text-sand transition-colors duration-500"
              >
                <div className="flex items-center gap-4 mb-8">
                  <Icon
                    className="w-7 h-7 text-rust group-hover:text-ochre transition-colors"
                    strokeWidth={1.5}
                  />
                  <span className="text-[11px] uppercase tracking-[0.28em] text-rust group-hover:text-ochre transition-colors tick-num">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight">
                  {m.title}
                </h3>
                <p className="mt-5 text-base leading-relaxed group-hover:text-sand/80 transition-colors">
                  {m.body}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* STRATEGIC OUTCOMES */}
      <Section
        eyebrow="03 · Strategic Outcomes"
        tone="alt"
        data-testid="briefing-outcomes"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 md:sticky md:top-28">
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
              What the initiative
              <span className="block italic text-rust">delivers.</span>
            </h2>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-ink max-w-md">
              Eleven measurable outcomes across food security, health, climate
              resilience, biodiversity, ecosystem restoration, economic
              participation and community-led development.
            </p>
          </div>

          <div className="md:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-stone-line border border-stone-line">
              {strategic.map((s, i) => (
                <li
                  key={s}
                  data-testid={`outcome-${i}`}
                  className="bg-sand p-6 md:p-7 flex items-start gap-4 hover:bg-charcoal hover:text-sand transition-colors duration-500 group"
                >
                  <CheckCircle2
                    className="w-5 h-5 text-rust group-hover:text-ochre transition-colors shrink-0 mt-1"
                    strokeWidth={1.5}
                  />
                  <span className="font-serif text-xl leading-snug">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* POLICY ALIGNMENT */}
      <Section
        eyebrow="04 · Policy Alignment"
        data-testid="briefing-alignment"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
              Aligned with national
              <span className="block italic text-sage">
                strategy and priorities.
              </span>
            </h2>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-ink">
              The initiative also supports broader national priorities across
              health, agriculture and regional development by strengthening
              local food production, improving nutrition outcomes and supporting
              sustainable economic activity in remote regions.
            </p>
          </div>

          <div className="md:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.28em] text-sage mb-6">
              Strategic Alignment
            </div>
            <ol className="space-y-px bg-stone-line border border-stone-line">
              {policy.map((p, i) => (
                <li
                  key={p}
                  data-testid={`alignment-${i}`}
                  className="bg-sand p-6 md:p-7 flex items-start gap-5 hover:bg-stone-alt transition-colors"
                >
                  <span className="font-serif text-2xl text-sage tick-num leading-none shrink-0 w-10">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-lg md:text-xl leading-snug text-charcoal pt-1">
                    {p}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </Section>

      {/* PILOT PROPOSAL */}
      <Section
        eyebrow="05 · Pilot Proposal"
        tone="dark"
        data-testid="briefing-pilot"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-7">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-sand">
              A four-year national
              <span className="block italic text-ochre">pilot program.</span>
            </h2>
            <div className="mt-10 space-y-6 text-base md:text-lg leading-relaxed text-sand/80 max-w-2xl">
              <p>
                Growing Country proposes an initial four-year pilot program
                across approximately twelve remote communities.
              </p>
              <p>
                The pilot is designed as a practical and scalable model that
                allows government and partners to test long-term approaches to
                Indigenous-led food resilience and local production systems.
              </p>
            </div>
          </div>

          <div className="md:col-span-4 md:col-start-9">
            <div className="grid grid-cols-2 gap-6 text-sand">
              <PilotStat n="4yr" l="Pilot horizon" />
              <PilotStat n="12+" l="Communities" />
              <PilotStat n="10" l="Core components" />
              <PilotStat n="∞" l="Replicable & scalable" />
            </div>
          </div>
        </div>

        <div className="border-t border-sand/15 pt-12">
          <div className="text-[11px] uppercase tracking-[0.28em] text-ochre/85 mb-8">
            Indicative pilot components
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-sand/10 border border-sand/15">
            {pilotComponents.map((c, i) => (
              <div
                key={c}
                data-testid={`pilot-component-${i}`}
                className="bg-charcoal p-6 md:p-8 flex items-start gap-5"
              >
                <span className="font-serif text-3xl text-ochre tick-num leading-none shrink-0 w-12">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif text-xl text-sand leading-snug">
                  {c}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* WHY THIS MATTERS */}
      <Section
        eyebrow="06 · Why This Matters"
        data-testid="briefing-why"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
              Food security is not simply a supply chain issue.
              <span className="block italic text-rust">
                It is a resilience issue.
              </span>
            </h2>
            <div className="mt-10 space-y-6 text-base md:text-lg leading-relaxed text-ink max-w-2xl">
              <p>
                Remote communities experience the impacts of freight
                disruption, rising costs, extreme weather events and climate
                change earlier and more intensely than most Australians.
              </p>
              <p>
                Strengthening local food production capacity creates a buffer
                against these disruptions while building community capability
                and self-determination.
              </p>
              <p>
                Growing Country recognises that resilient food systems depend
                upon healthy soils, reliable water, diverse food production
                systems and local knowledge.
              </p>
              <p>
                By investing in regenerative orchards, food forests, seed
                systems, soil biology and community-led growing initiatives,
                Australia can support a new generation of climate-resilient
                food infrastructure across remote regions.
              </p>
              <p>
                The benefits extend beyond nutrition. These systems support
                biodiversity, cooling landscapes, cultural knowledge transfer,
                youth engagement, local employment and stronger connections
                between people, food and Country.
              </p>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="bg-charcoal text-sand p-10 md:p-14 relative overflow-hidden">
              <div className="absolute inset-0 grain-overlay opacity-40" />
              <div className="relative">
                <div
                  aria-hidden
                  className="font-serif text-rust text-6xl leading-none mb-3 select-none"
                >
                  "
                </div>
                <blockquote
                  data-testid="briefing-pullquote"
                  className="font-serif text-2xl md:text-3xl font-light leading-snug"
                >
                  Growing Country is not simply about growing food.
                  <span className="block mt-4 italic text-ochre">
                    It is about rebuilding local capability, strengthening
                    community resilience and restoring relationships between
                    people, food and Country.
                  </span>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ABOUT WILLIAM MARTIN */}
      <Section
        eyebrow="07 · About the Author"
        tone="alt"
        data-testid="briefing-author"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.28em] text-rust mb-5">
              William Martin
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
              Years of work
              <span className="block italic text-rust">on Country.</span>
            </h2>
          </div>

          <div className="md:col-span-7 space-y-6 text-base md:text-lg leading-relaxed text-ink">
            <p>
              William Martin has spent many years living and working alongside
              remote Aboriginal communities across Australia.
            </p>
            <p>
              His work has included community agriculture, native seed systems,
              ecological restoration, seed saving, ranger support programs and
              remote food production initiatives.
            </p>
            <p>
              With support from the National Indigenous Australians Agency and
              the Indigenous Land and Sea Corporation, William helped establish
              greenhouses, seed banks and community garden initiatives across
              remote communities in the Northern Territory.
            </p>
            <p>
              His work combines practical experience in remote community
              development, ecological restoration, Indigenous land management
              and climate resilience.
            </p>

            <div className="pt-8 border-t border-stone-line/70 grid grid-cols-2 gap-x-8 gap-y-6">
              {[
                { k: "Focus", v: "Indigenous food systems" },
                { k: "Region", v: "Northern Territory · APY Lands · NPY Lands & beyond" },
                { k: "Backed by", v: "NIAA · ILSC" },
                { k: "Practice", v: "Ecological restoration · Seed systems · Land management" },
              ].map((m) => (
                <div key={m.k}>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-ink/60 mb-1">
                    {m.k}
                  </div>
                  <div className="font-serif text-lg text-charcoal">
                    {m.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* PARTNERSHIP OPPORTUNITIES */}
      <Section
        eyebrow="08 · Partnership Opportunities"
        data-testid="briefing-partners"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-14">
          <div className="md:col-span-7">
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
              Growing Country welcomes
              <span className="block italic text-sage">
                conversations across sectors.
              </span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-stone-line border border-stone-line">
          {partners.map((p, i) => (
            <div
              key={p}
              data-testid={`partner-${i}`}
              className="bg-sand p-7 md:p-8 flex items-center gap-4 hover:bg-charcoal hover:text-sand transition-colors duration-500 group"
            >
              <Building2
                className="w-5 h-5 text-sage group-hover:text-ochre transition-colors shrink-0"
                strokeWidth={1.5}
              />
              <span className="font-serif text-lg md:text-xl leading-snug">
                {p}
              </span>
            </div>
          ))}
        </div>
      </Section>

      {/* CONTACT */}
      <section
        id="briefing-contact"
        data-testid="briefing-contact"
        className="relative bg-charcoal text-sand py-24 md:py-36 overflow-hidden"
      >
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-6">
              <div className="text-[11px] uppercase tracking-[0.3em] text-ochre mb-6">
                09 · Contact
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
                Start the
                <span className="block italic text-ochre">conversation.</span>
              </h2>
              <p className="mt-8 max-w-lg text-base md:text-lg leading-relaxed text-sand/75">
                Growing Country — Building Australia's Indigenous Food Future.
                For partnership discussions, briefings or community
                conversations, please get in touch directly.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href={PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="briefing-footer-download"
                  className="inline-flex items-center gap-3 bg-rust text-sand px-7 py-4 text-xs uppercase tracking-[0.22em] hover:bg-rust-dark transition-colors"
                >
                  <Download className="w-4 h-4" strokeWidth={1.5} />
                  Download briefing (PDF)
                </a>
                <Link
                  to="/#contact"
                  data-testid="briefing-footer-cta"
                  className="inline-flex items-center gap-3 border border-sand/60 text-sand px-7 py-4 text-xs uppercase tracking-[0.22em] hover:bg-sand hover:text-charcoal transition-colors"
                >
                  Send a message
                </Link>
              </div>
            </div>

            <div className="md:col-span-5 md:col-start-8 border-l border-ochre/30 pl-8 space-y-7">
              <ContactRow
                icon={Mail}
                label="Email"
                value="william@growingcountry.org"
                href="mailto:william@growingcountry.org"
                testid="briefing-contact-email"
              />
              <ContactRow
                icon={Phone}
                label="Phone"
                value="0411 798 772"
                href="tel:+61411798772"
                testid="briefing-contact-phone"
              />
              <ContactRow
                icon={Globe}
                label="Online"
                value="www.growingcountry.org"
                href="https://www.growingcountry.org"
                testid="briefing-contact-web"
              />
              <div className="pt-6 border-t border-sand/15">
                <div className="text-[11px] uppercase tracking-[0.25em] text-ochre/80 mb-2">
                  Authored by
                </div>
                <div className="font-serif text-2xl text-sand">
                  William Martin
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

/* ---------------- Sub-components ---------------- */

function Meta({ k, v }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.25em] text-sand/50 mb-1">
        {k}
      </div>
      <div className="font-serif text-lg text-sand leading-snug">{v}</div>
    </div>
  );
}

function Section({ id, eyebrow, tone = "light", children, ...rest }) {
  const bg =
    tone === "alt"
      ? "bg-stone-alt"
      : tone === "dark"
      ? "bg-charcoal text-sand"
      : "bg-sand";
  return (
    <section
      id={id}
      {...rest}
      className={`relative ${bg} py-24 md:py-32 overflow-hidden`}
    >
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {eyebrow && (
          <div
            className={`text-[11px] uppercase tracking-[0.3em] mb-10 ${
              tone === "dark" ? "text-ochre" : "text-rust"
            }`}
          >
            {eyebrow}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

function PilotStat({ n, l }) {
  return (
    <div className="border-t border-sand/20 pt-5">
      <div className="font-serif text-3xl md:text-4xl text-ochre tick-num leading-none">
        {n}
      </div>
      <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-sand/65 leading-snug">
        {l}
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  return (
    <div className="flex items-start gap-5">
      <Icon className="w-5 h-5 text-ochre mt-1 shrink-0" strokeWidth={1.5} />
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-sand/55 mb-1">
          {label}
        </div>
        <a
          href={href}
          data-testid={testid}
          target={href?.startsWith("http") ? "_blank" : undefined}
          rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
          className="font-serif text-xl md:text-2xl text-sand text-link"
        >
          {value}
        </a>
      </div>
    </div>
  );
}
